import * as Yup from "yup";
import {
    dropDownErrorMsg,
    lengthErrorMsg,
    maxLengthErrorMsg,
    minLengthErrorMsg,
    ifscLengthErrorMsg
} from "../utils/constants/YupErrorMessages";
import {ascii} from "../utils/utils";
import {defaultAddressValidationSchema} from "../utils/constants/AddressFields";
import {getDateWithDelta} from "./utils";

export const remitterValidationSchema = Yup.object().shape({
    honorific: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Honorific is required'),
    name: ascii.uppercase().required('Name is required'),
    // eslint-disable-next-line no-undef
    pan_tan: ascii.length(10, lengthErrorMsg).uppercase().required('PAN is required'),
    bank_name: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Bank Name is required'),
    bank_name_other: ascii.uppercase().when('bank_name', {
        is: val => val === '999',
        then: schema => schema.required('Bank Name other is required'),
        otherwise: schema => schema.nullable()
    }),
    branch_name: ascii.uppercase().required('Branch name is required'),
    bsr_code: ascii.max(7, maxLengthErrorMsg).min(7, minLengthErrorMsg).required('BSR Code is required'),
    ifsc_code: ascii.max(11, ifscLengthErrorMsg).min(11, ifscLengthErrorMsg).required('IFSC Code is required'),
    is_ca_applicable: Yup.string().required(),
    is_bank_and_dealer_same: Yup.string().required(),
    area_code: ascii.max(3, maxLengthErrorMsg).uppercase().nullable(),
    ao_type: ascii.max(2, maxLengthErrorMsg).uppercase().nullable(),
    range_code: ascii.matches(/^\d+$/, {message: 'Range Code should be digits only', excludeEmptyString: true}).nullable(),
    ao_number: ascii.matches(/^\d+$/, {message: 'AO Number should be digits only', excludeEmptyString: true}).nullable(),
    flat_door_building: ascii.required('Flat/Foor/Building is required'),
    premise_building_village: ascii.required('Premise/Building/Village is required'),
    road_street: ascii.required('Road/Street/Block/Sector is required'),
    post_office: ascii.required('Post Office is required'),
    area_locality: ascii.required('Area/Locality is required'),
    town_city_district: ascii.required('Town/City/District is required'),
    state: ascii.notOneOf(["-1"], dropDownErrorMsg).required('State is required'),
    pincode: ascii.max(6, maxLengthErrorMsg).min(6, minLengthErrorMsg).required('Pincode is required'),
    email: ascii.email().required('Email is required'),
    // Optional, but still has to be a real address if one is typed - it is filed.
    secondary_email: ascii.email().nullable(),
    // The remitter's second number, sent as entitySecondaryMobile on 15CA Part C only.
    // Optional, like secondary_email beside it: no remitter created before 8 Sep 2026
    // has one. excludeEmptyString keeps the pattern off a blank.
    secondary_phone: ascii.max(10, maxLengthErrorMsg)
        .matches(/^\d{10}$/, {message: 'Phone Number not in correct format', excludeEmptyString: true})
        .nullable(),
    phone: ascii.max(10, maxLengthErrorMsg).min(10, minLengthErrorMsg).required('Phone is required'),
    phone_code: ascii.nullable(),
    status: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Status is required'),
    residential_status: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Residential Status is required'),
    country: ascii.required('Country is required'),
    authorized_dealer_name: ascii.uppercase().when('is_bank_and_dealer_same', {
        is: val => val === 'N',
        then: schema => schema.required('Authorized Dealer Name is required'),
        otherwise: schema => schema.nullable()
    }),
    // Optional - the government form does not mark ITDREIN mandatory either.
    itdrein: ascii.uppercase().nullable(),
    place_of_business: ascii.uppercase().when('is_ca_applicable', {
        is: val => val === 'Y',
        then: schema => schema.required('Place of Business is required'),
        otherwise: schema => schema.nullable()
    }),
    verification_person_name: ascii.when('is_ca_applicable', {
        is: val => val === 'Y',
        then: schema => schema.required('Person Name is required'),
        otherwise: schema => schema.nullable()
    }),
    // The PAN of the person signing for the remitter. 15CA Part C sends it three
    // times - pcPan, userPan and form19BF7PartCP19VerPan - and Form 146 does not use
    // it at all: 15CB is signed by the CA, so its userPan is the accountant's.
    //
    // Optional even when 15CA applies, unlike the name beside it: every remitter
    // created before 8 Sep 2026 has no value here, and requiring one would make all
    // of them unsaveable on a screen they had been passing for months.
    // excludeEmptyString matters for the same reason - without it the pattern runs
    // against '' and blocks the save.
    verification_person_pan: ascii.max(10, maxLengthErrorMsg).uppercase()
        .matches(/^[A-Z]{5}\d{4}[A-Z]$/, {message: 'PAN not in correct format', excludeEmptyString: true})
        .nullable(),
    verification_i_we: ascii.when('is_ca_applicable', {
        is: val => val === 'Y',
        then: schema => schema.notOneOf(["-1"], dropDownErrorMsg).required('Verification I/We is required'),
        otherwise: schema => schema.nullable()
    }),
    verification_designation: ascii.when('is_ca_applicable', {
        is: val => val === 'Y',
        then: schema => schema.required('Designation is required'),
        otherwise: schema => schema.nullable()
    }),
    verification_place: ascii.when('is_ca_applicable', {
        is: val => val === 'Y', then: schema => schema.required('Place is required'), otherwise: schema => schema.nullable()
    })
});

export const remitteeValidationSchema = defaultAddressValidationSchema.concat(Yup.object().shape({
    honorific: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Honorific Details is required'),
    name: ascii.uppercase().required('Name is required'),
    zipcode: ascii.required('Zipcode is required'),
    // A remittee is foreign, so State/Province is a free-text box and optional: this
    // matches the Remittee model (CharField 125, blank=True) and the Remittee master
    // screen. Do NOT restore the 4-character coded-state rule - it silently rejects
    // any real place name (SINGAPORE, CALIFORNIA) that the master screen accepts.
    state: ascii.nullable(),
    // TIN is a foreign identifier - lengths vary by country, so no length rule at all
    // (BGSS, 16 Sep). The Remittee model is a TextField to match.
    tin_number: ascii.required('TIN Number is required'),
    country_of_remittance: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Country of Remittance'),
    email: ascii.email().nullable(),
    phone: ascii.nullable(),
    phone_code: ascii.nullable(),
    country: ascii.nullable(),
    premise_building_village: ascii.nullable(),
    // Optional for a foreign address - many have no street line, the model allows
    // blank, and the preview simply drops empty parts of the address.
    road_street: ascii.nullable(),
    // Watches country_of_remittance, not currency - '9999' is a country code, so
    // keyed off currency this condition could never be true and the box was never
    // enforced. Same mistake was in nature_of_remittance_other below.
    country_of_remittance_other: ascii.when('country_of_remittance', {
        is: val => val === '9999',
        then: schema => schema.required('Country of Remittance (Other) is required'),
        otherwise: schema => schema.nullable()
    }),
    currency_of_remittance: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Currency of Remittance'),
    currency_of_remittance_other: ascii.uppercase().when('currency_of_remittance', {
        is: val => val === '99',
        then: schema => schema.required('Currency of Remittance (Other)'),
        otherwise: schema => schema.nullable()
    }),
    nature_of_remittance: ascii.min(4, minLengthErrorMsg).notOneOf(["-1"], dropDownErrorMsg).required('Nature of Remittance'),
    nature_of_remittance_other: ascii.uppercase().when('nature_of_remittance', {
        is: val => val === '16.99',
        then: schema => schema.required('Nature of Remittance (Other) is required'),
        otherwise: schema => schema.nullable()
    }),
    relevant_purpose_code: ascii.min(6, minLengthErrorMsg).notOneOf(["-1"], dropDownErrorMsg).required('Relevant Purpose Code'),
    relevant_purpose_code_detail: ascii.min(12, minLengthErrorMsg).notOneOf(["-1"], dropDownErrorMsg).required('Relevant Purpose Code Detail'),
    // PAN is "if available" for a remittee: an empty box must pass. A bare .length(10)
    // rejects '', which is what the API returns for a remittee with no PAN.
    pan: ascii.uppercase().nullable().test('pan-length', 'Length should be 10', value => !value || value.length === 10),
    principal_place_of_business: ascii.uppercase().required('Principal Place of Business is Required'),
    country_of_residence: ascii.notOneOf(["-1"], dropDownErrorMsg).required('Country of Residence'),
    post_office: ascii.required('Post Office is required')
}));


export const accountantValidationSchema = defaultAddressValidationSchema.concat(
    Yup.object().shape({
        name: ascii.uppercase('Name should be in uppercase').required('Name is required'),
        firm: ascii.uppercase('Firm should be in uppercase').required('Firm is required'),
        membership_number: ascii.length(6, lengthErrorMsg).required('Membership Number is required'),
        registration_number: ascii.length(8, lengthErrorMsg).required('Registration Number is required'),
        email: Yup.string().email('Enter a valid Email ID').required('Email is Required'),
        // Typed on this step, per certificate, because the address changes from
        // client to client. Compulsory: the utility renders it read-only on the
        // verification page and files whatever it is given without checking - proven
        // 22 Aug 2026, when it accepted a literal 'x' and saved the form. This and
        // its server-side twin are the only controls that exist.
        certification_ip: Yup.string()
            .required('IP Address is required')
            .test(
                'public-ipv4',
                'Enter the public IP address, for example 122.179.152.142',
                value => {
                    if (!value) return false;
                    const parts = value.trim().split('.');
                    if (parts.length !== 4) return false;
                    if (!parts.every(p => /^\d{1,3}$/.test(p) && Number(p) <= 255)) return false;
                    const [a, b] = parts.map(Number);
                    if (a === 10 || a === 127 || a === 0) return false;     // private, loopback
                    if (a === 192 && b === 168) return false;               // private
                    if (a === 172 && b >= 16 && b <= 31) return false;      // private
                    if (a === 169 && b === 254) return false;               // link-local
                    return true;
                }
            ),
        state: Yup.string().notOneOf(['-1'], 'Select a value').required('State is required'),
        pincode: Yup.string().matches(/\d{6}/, 'Pincode should be 6 digits').required('Pincode is required')
    })
);

export const invoiceValidationSchema = Yup.object().shape({
    invoices: Yup.array().of(Yup.object().shape({
        invoice_number: Yup.string().required('Invoice Number is required'),
        invoice_file: Yup.mixed().required('Invoice file is required'),
        // Yup's required() rejects an empty box but accepts 0 and accepts a negative,
        // while the computation serializer requires more than zero on both. So a zero
        // or negative figure passed this step and was only refused later, by Get
        // Computation, on the screen after. Refuse it here, where the box is.
        invoice_amount: Yup.number().required('Invoice Amount is required')
            .moreThan(0, 'Invoice Amount must be more than zero'),
        invoice_date: Yup.date().required('Invoice Date is required'),
        exchange_rate: Yup.number().required('Exchange Rate is required')
            .moreThan(0, 'Exchange Rate must be more than zero'),
        exchange_date: Yup.date().required('Exchange Date is required')
    })).min(1, 'Atleast one Invoice should be present')
});

export const remittanceValidationSchema = Yup.object().shape({

    // certification_ip is validated on the accountant step, not here - it moved to
    // Details of Accountant, the section it belongs to on the certificate.

    // Remittance Details
    is_system_calculation_applicable: Yup.string().required('System CalculationForm is required?'),
    rounding: Yup.number().when('is_system_calculation_applicable', {
        is: is_system_calculation_applicable => is_system_calculation_applicable === 'Y',
        then: schema => schema.required(),
        otherwise: schema => schema.nullable()
    }).label('Rounding scheme'),
    amt_payable_inr: Yup.number().when('is_system_calculation_applicable', {
        is: is_system_calculation_applicable => is_system_calculation_applicable === 'N',
        then: schema => schema.required('Amount Payable (Indian Currency) is required'),
        otherwise: schema => schema.nullable()
    }),
    amt_payable_frgn: Yup.number().when('is_system_calculation_applicable', {
        is: is_system_calculation_applicable => is_system_calculation_applicable === 'N',
        then: schema => schema.required('Amount Payable (Foreign Currency) is required'),
        otherwise: schema => schema.nullable()
    }),
    exchange_rate: Yup.number().when('is_system_calculation_applicable', {
        is: is_system_calculation_applicable => is_system_calculation_applicable === 'N',
        then: schema => schema.required('Exchange Rate is required'),
        otherwise: schema => schema.nullable()
    }),
    expenses_reimbursement_frgn: Yup.number().nullable(),
    proposed_date_of_remittance: Yup.date().min(getDateWithDelta(0), 'Either today or future date').required('Proposed date of remittance is required'),
    // This records when the client's mail actually arrived, so it is normally in the
    // past. It used to demand today or later, which made a real received date
    // impossible to enter.
    mail_received: Yup.date().max(getDateWithDelta(0), 'Either today or a past date').required('Mail Received is required'),
    is_grossed_up: Yup.string().required('Is Grossed Up required'),
    
    // Income-Tax act
    is_it_taxable: Yup.string().required('Is taxable under Income-tax required?'),
    it_relevant_section_act: ascii.when('is_it_taxable', {
        is: val => val === 'Y',
        then: schema => schema.required('Relevant Section is Required'),
        otherwise: schema => schema.nullable()
    }),
    it_basis: ascii.when('is_it_taxable', {
        is: val => val === 'Y',
        then: schema => schema.required('IT Basis is Required'),
        otherwise: schema => schema.nullable()
    }),
    it_taxable_income: Yup.number().when(['is_it_taxable', 'is_system_calculation_applicable'], {
        is: (is_it_taxable, is_system_calculation_applicable) => is_it_taxable === 'Y' && is_system_calculation_applicable === 'N',
        then: schema => schema.required('Taxable Income is Required'),
        otherwise: schema => schema.nullable()
    }),
    it_tax_liability: Yup.number().when(['is_it_taxable', 'is_system_calculation_applicable'], {
        is: (is_it_taxable, is_system_calculation_applicable) => is_it_taxable === 'Y' && is_system_calculation_applicable === 'N',
        then: schema => schema.required('Tax Liability is Required'),
        otherwise: schema => schema.nullable()
    }),
    // Asked in manual mode too: the 146 JSON's Income-tax rate (form19bf8Ratededctax)
    // fell back to the DTAA rate when this was blank (BGSS, Chiba Bank, 5 Oct).
    it_rate: Yup.number().when(['is_it_taxable'], {
        is: (is_it_taxable) => is_it_taxable === 'Y',
        then: schema => schema.required('Tax Rate is Required'),
        otherwise: schema => schema.nullable()
    }),
    it_not_taxable_reason: ascii.when('is_it_taxable', {
        is: (is_it_taxable) => is_it_taxable === 'N',
        then: schema => schema.required('Reason for Non-Taxability is required'),
        otherwise: schema => schema.nullable()
    }),

    // DTAA
    is_dtaa_taxable: Yup.string().when('is_it_taxable', {
        is: (is_it_taxable) => is_it_taxable === 'Y',
        then: schema => schema.required('Is DTAA Taxable is required'),
        otherwise: schema => schema.nullable()
    }),
    has_tax_residency_certificate: ascii.when(['is_it_taxable'], {
        is: (is_it_taxable) => is_it_taxable === 'Y',
        then: schema => schema.required('Tax Residency Certificate is Required'),
        otherwise: schema => schema.nullable()
    }),
    tax_residency_certificate_number: ascii.when(['is_it_taxable', 'has_tax_residency_certificate'], {
        is: (is_it_taxable, has_tax_residency_certificate) => is_it_taxable === 'Y' && has_tax_residency_certificate === 'Y',
        then: schema => schema.required('Tax Residency Certificate Number is Required'),
        otherwise: schema => schema.nullable()
    }),
    // Treaty names run past 30 characters ("INDIA - SWISS CONFEDERATION DTAA") - no
    // length rule at all, per BGSS (16 Sep). Cb.dtaa_act is a TextField to match.
    dtaa_act: ascii.nullable(),
    dtaa_article: ascii.nullable(),
    // Compulsory once relief is claimed. Until 14 Sep 2026 a blank here went out on
    // 15CA Part C as the word "Dividend" - a nature of payment invented on a tax
    // certificate. Every one of BGSS's files has it filled, so a blank is a slip, and a
    // slip is for the CA to correct, not the file to paper over.
    nature_of_payment_as_per_dtaa: ascii.when(['is_dtaa_taxable', 'is_it_taxable'], {
        is: (is_dtaa_taxable, is_it_taxable) => is_dtaa_taxable === 'Y' && is_it_taxable === 'Y',
        then: schema => schema.required('Nature of Payment as per DTAA is Required'),
        otherwise: schema => schema.nullable()
    }),
    dtaa_taxable_income: Yup.number().when(['is_it_taxable', 'is_system_calculation_applicable'], {
        is: (is_it_taxable, is_system_calculation_applicable) => is_it_taxable === 'Y' && is_system_calculation_applicable === 'N',
        then: schema => schema.required('Taxable Income is Required'),
        otherwise: schema => schema.nullable()
    }),
    dtaa_tax_liability: Yup.number().when(['is_it_taxable', 'is_system_calculation_applicable'], {
        is: (is_it_taxable, is_system_calculation_applicable) => is_it_taxable === 'Y' && is_system_calculation_applicable === 'N',
        then: schema => schema.required('Tax Liability is Required'),
        otherwise: schema => schema.nullable()
    }),
    dtaa_rate: Yup.number().when(['is_dtaa_taxable', 'is_it_taxable','is_system_calculation_applicable'], {
        is: (is_dtaa_taxable, is_it_taxable, is_system_calculation_applicable) => is_dtaa_taxable === 'Y' && is_it_taxable === 'Y' && is_system_calculation_applicable === 'Y',
        then: schema => schema.required('Tax Rate is Required'),
        otherwise: schema => schema.nullable()
    }),

    // DTAA Section
    dtaa_section: Yup.string().when(['is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable'], ([is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable], schema) => {
        if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
            if (is_system_calculation_applicable === 'Y') {
                // Section C (capital gains) is not supported by the system calculation.
                // Saying so beats "Please select a value" on a box already filled in.
                // A separate named test, not a second notOneOf - yup merges those into
                // one blacklist and keeps only the last message.
                return schema
                    .notOneOf(['0'], dropDownErrorMsg)
                    .test('dtaa-section-c-needs-manual', 'Section C (capital gains) needs system calculation switched off', value => value !== '3')
                    .required('DTAA Section is required')
            }
            return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section is required')
        } else {
            return schema.nullable()
        }
    }),

    // DTAA Section A
    is_such_remittance_taxable_a: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable], schema) => {
        if (dtaa_section === '1' && is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
            if (is_system_calculation_applicable === 'Y') {
                return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section A remittance taxable is required')
            }
            return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section A remittance taxable is required')
        } else {
            return schema.nullable()
        }
    }),
    dtaa_sect_article_a: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_a'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_a], schema) => {
        if(dtaa_section === '1' && is_such_remittance_taxable_a === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section A article dtaa is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section A article dtaa is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    amount_taxable_dtaa_a: Yup.number().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_a'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_a], schema) => {
        if(dtaa_section === '1' && is_such_remittance_taxable_a === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section A amount of taxable is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section A amount of taxable is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    dtaa_sect_rate_a: Yup.number().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_a'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_a], schema) => {
        if(dtaa_section === '1' && is_such_remittance_taxable_a === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section A rate of deduction is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section A rate of deduction is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    furnish_brief_reasons_dtaa_a: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_a'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_a], schema) => {
        if(dtaa_section === '1' && is_such_remittance_taxable_a === 'N') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section A brief reasons is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section A brief reasons is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),

    // DTAA Section B
    is_such_remittance_taxable_b: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable], schema) => {
        if (dtaa_section === '2' && is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
            if (is_system_calculation_applicable === 'Y') {
                return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section B remittance taxable is required')
            }
            return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section B remittance taxable is required')
        } else {
            return schema.nullable()
        }
    }),
    dtaa_sect_article_b: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_b'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_b], schema) => {
        if(dtaa_section === '2' && is_such_remittance_taxable_b === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section B article dtaa is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section B article dtaa is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    amount_taxable_dtaa_b: Yup.number().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_b'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_b], schema) => {
        if(dtaa_section === '2' && is_such_remittance_taxable_b === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section B amount of taxable is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section B amount of taxable is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    dtaa_sect_rate_b: Yup.number().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_b'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_b], schema) => {
        if(dtaa_section === '2' && is_such_remittance_taxable_b === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section B rate of deduction is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section B rate of deduction is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    furnish_brief_reasons_dtaa_b: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_b'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_b], schema) => {
        if(dtaa_section === '2' && is_such_remittance_taxable_b === 'N') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section B brief reasons is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section B brief reasons is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),

    // DTAA Section C
    is_such_remittance_taxable_c: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_it_taxable'], ([dtaa_section, is_dtaa_taxable, is_it_taxable], schema) => {
        if (dtaa_section === '3' && is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
            return schema.notOneOf(['-1', '0'], dropDownErrorMsg).required('DTAA Section C remittance taxable is required')
        }
        return schema.nullable()
    }),
    dtaa_sect_c_amt_long_term: Yup.number().when(['is_dtaa_taxable', 'dtaa_section', 'is_it_taxable', 'is_such_remittance_taxable_c'], {
        is: (is_dtaa_taxable, dtaa_section, is_it_taxable, is_such_remittance_taxable_c) =>
            is_dtaa_taxable === 'Y' && dtaa_section === '3' && is_it_taxable === 'Y' && is_such_remittance_taxable_c === 'Y',
        then: schema => schema.required('Long term capital gains amount is required'),
        otherwise: schema => schema.nullable()
    }),
    dtaa_sect_c_amt_short_term: Yup.number().when(['is_dtaa_taxable', 'dtaa_section', 'is_it_taxable', 'is_such_remittance_taxable_c'], {
        is: (is_dtaa_taxable, dtaa_section, is_it_taxable, is_such_remittance_taxable_c) =>
            is_dtaa_taxable === 'Y' && dtaa_section === '3' && is_it_taxable === 'Y' && is_such_remittance_taxable_c === 'Y',
        then: schema => schema.required('Short term capital gains amount is required'),
        otherwise: schema => schema.nullable()
    }),
    dtaa_sect_c_basis: ascii.when(['is_dtaa_taxable', 'dtaa_section', 'is_it_taxable', 'is_such_remittance_taxable_c'], {
        is: (is_dtaa_taxable, dtaa_section, is_it_taxable, is_such_remittance_taxable_c) =>
            is_dtaa_taxable === 'Y' && dtaa_section === '3' && is_it_taxable === 'Y' && is_such_remittance_taxable_c === 'Y',
        then: schema => schema.required('Section C basis is required'),
        otherwise: schema => schema.nullable()
    }),
    furnish_brief_reasons_dtaa_c: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_it_taxable', 'is_such_remittance_taxable_c'], ([dtaa_section, is_dtaa_taxable, is_it_taxable, is_such_remittance_taxable_c], schema) => {
        if (dtaa_section === '3' && is_such_remittance_taxable_c === 'N' && is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
            return schema.required('DTAA Section C brief reasons is required')
        }
        return schema.nullable()
    }),

    // DTAA Section D
    is_such_remittance_taxable_d: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable], schema) => {
        if (dtaa_section === '4' && is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
            if (is_system_calculation_applicable === 'Y') {
                return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section D remittance taxable is required')
            }
            return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section D remittance taxable is required')
        } else {
            return schema.nullable()
        }
    }),
    dtaa_sect_article_d: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_d'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_d], schema) => {
        if(dtaa_section === '4' && is_such_remittance_taxable_d === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section D article dtaa is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section D article dtaa is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    amount_taxable_dtaa_d: Yup.number().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_d'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_d], schema) => {
        if(dtaa_section === '4' && is_such_remittance_taxable_d === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section D amount of taxable is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section D amount of taxable is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    dtaa_sect_rate_d: Yup.number().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_d'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_d], schema) => {
        if(dtaa_section === '4' && is_such_remittance_taxable_d === 'Y') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section D rate of deduction is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section D rate of deduction is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),
    furnish_brief_reasons_dtaa_d: Yup.string().when(['dtaa_section', 'is_dtaa_taxable', 'is_system_calculation_applicable', 'is_it_taxable', 'is_such_remittance_taxable_d'], ([dtaa_section, is_dtaa_taxable, is_system_calculation_applicable, is_it_taxable, is_such_remittance_taxable_d], schema) => {
        if(dtaa_section === '4' && is_such_remittance_taxable_d === 'N') {
            if (is_dtaa_taxable === 'Y' && is_it_taxable === 'Y') {
                if (is_system_calculation_applicable === 'Y') {
                    return schema.notOneOf(['0', '3'], dropDownErrorMsg).required('DTAA Section D brief reasons is required')
                }
                return schema.notOneOf(['0'], dropDownErrorMsg).required('DTAA Section D brief reasons is required')
            } else {
                return schema.nullable()
            }
        } else {
            return schema.nullable()
        }
    }),

    // TDS Details
    tds_amt_frgn: Yup.number().when(['is_system_calculation_applicable', 'is_it_taxable'], {
        is: (is_system_calculation_applicable, is_it_taxable) => is_system_calculation_applicable === 'N' && is_it_taxable === 'Y',
        then: schema => schema.required('TDS Amount (Foreign) is required'),
        otherwise: schema => schema.nullable()
    }),
    tds_amt_inr: Yup.number().when(['is_system_calculation_applicable', 'is_it_taxable'], {
        is: (is_system_calculation_applicable, is_it_taxable) => is_system_calculation_applicable === 'N' && is_it_taxable === 'Y',
        then: schema => schema.required('TDS Amount (Indian) is required'),
        otherwise: schema => schema.nullable()
    }),
    tds_rate_as_per: Yup.number().when(['is_system_calculation_applicable', 'is_it_taxable'], {
        is: (is_system_calculation_applicable, is_it_taxable) => is_system_calculation_applicable === 'N' && is_it_taxable === 'Y',
        then: schema => schema.notOneOf(['-1'], dropDownErrorMsg).required('TDS Rate As Per is required'),
        otherwise: schema => schema.nullable()
    }),
    tds_rate: Yup.number().when(['is_system_calculation_applicable', 'is_it_taxable'], {
        is: (is_system_calculation_applicable, is_it_taxable) => is_system_calculation_applicable === 'N' && is_it_taxable === 'Y',
        then: schema => schema.required('TDS Rate is required'),
        otherwise: schema => schema.nullable()
    }),
    actual_amt_after_tds_frgn: Yup.number().when(['is_system_calculation_applicable', 'is_it_taxable'], {
        is: (is_system_calculation_applicable, is_it_taxable) => is_system_calculation_applicable === 'N' && is_it_taxable === 'Y',
        then: schema => schema.required('Actual Amount after TDS (Foreign) is required'),
        otherwise: schema => schema.nullable()
    }),
    actual_amt_after_tds_inr: Yup.number().when(['is_system_calculation_applicable', 'is_it_taxable'], {
        is: (is_system_calculation_applicable, is_it_taxable) => is_system_calculation_applicable === 'N' && is_it_taxable === 'Y',
        then: schema => schema.required('Actual Amount after TDS (Indian) is required'),
        otherwise: schema => schema.nullable()
    }),
    date_of_deduction: Yup.date().when(['tds_rate', 'is_system_calculation_applicable', 'is_it_taxable'], {
        is: (tds_rate, is_system_calculation_applicable, is_it_taxable) => is_it_taxable !== 'Y' || (tds_rate === 0 && is_system_calculation_applicable === 'Y'),
        then: schema => schema.nullable(),
        otherwise: schema => schema.max(getDateWithDelta(0), 'Either today or past date').required('Date of Deduction is required'),
    })
});

export const computationValidationSchema = Yup.object().shape({
    is_system_calculation_applicable: Yup.string().required('System CalculationForm is required?'),
    rounding: Yup.number().when('is_system_calculation_applicable', {
        is: is_system_calculation_applicable => is_system_calculation_applicable === 'Y',
        then: schema => schema.required(),
        otherwise: schema => schema.nullable()
    }).label('Rounding scheme'),
    expenses_reimbursement_frgn: Yup.number().nullable(),
    is_grossed_up: Yup.string().required('Is Grossed Up required'),
    is_it_taxable: Yup.string().required('Is taxable under Income-tax required?'),
    it_rate: Yup.number().when(['is_it_taxable'], {
        is: (is_it_taxable) => is_it_taxable === 'Y',
        then: schema => schema.required('Tax Rate is Required'),
        otherwise: schema => schema.nullable().transform((curr, orig) => orig === '' ? null : curr)
    }),
    is_dtaa_taxable: Yup.string().when('is_it_taxable', {
        is: (is_it_taxable) => is_it_taxable === 'Y',
        then: schema => schema.required('Is DTAA Taxable is required'),
        otherwise: schema => schema.nullable()
    }),
    dtaa_rate: Yup.number().when(['is_dtaa_taxable', 'is_it_taxable'], {
        is: (is_dtaa_taxable, is_it_taxable) => is_dtaa_taxable === 'Y' && is_it_taxable === 'Y',
        then: schema => schema.required('Tax Rate is Required'),
        otherwise: schema => schema.nullable().transform((curr, orig) => orig === '' ? null : curr)
    })
});


// Token Number is optional - "Save Token" moves the transaction on with the box
// left empty. It is not written to the government JSON or the certificate; it
// appears only on the Excel summary, so a blank one cannot reach a filing.
export const tokenValidationSchema = Yup.object().shape({
    token_number: Yup.string().nullable()
});


export const acknowledgementValidationSchema = Yup.object().shape({
    acknowledgement_number: Yup.string().required().label('Acknowledgement Number'),
    acknowledgement_date: Yup.date().required().label('Acknowledgement Date')
});


export const udinValidationSchema = Yup.object().shape({
    udin_number: Yup.string().required().label('UDIN Number'),
    udin_date: Yup.date().required().label('UDIN Date')
});

export const udinStatusSchema = Yup.object().shape({
    udin_status: Yup.string().notOneOf(['-1']).required().label('UDIN Status')
});

export const noteOnCertificationSchema = Yup.object().shape({
    note_on_certification: Yup.string().notOneOf(['-1']).required().label('Note on Certification')
});

export const commentSchema = Yup.object().shape({
    comments: Yup.string().nullable()
});

export const cancelSchema = Yup.object().shape({
    canceled_by: Yup.string().notOneOf(['-1'], dropDownErrorMsg).required().label('Cancellation source'),
});

export const updateStatusSchema = Yup.object().shape({
    status: Yup.string().notOneOf(['-1'], dropDownErrorMsg).required().label('Status')
})

export const mailSentSchema = Yup.object().shape({
    mail_sent: Yup.date().required().label('Mail Sent')
})
